import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import ProjectCard from './ProjectCard';
import { projectsData } from './projectsData';
import Computer from "../animationpics/projects.jsx";
import { motion, AnimatePresence } from 'framer-motion';
import './styles.css';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  height: auto;
  overflow: visible;
  background-color: transparent;
  padding-bottom: 50px;
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 20px 0;
  margin-bottom: 10px;
  margin-top: 20px; 

  @media (max-width: 768px) {
    margin-top: 40px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 20px;
  gap: 40px;
  align-items: flex-start;

  @media (max-width: 1024px) {
    flex-direction: column;
    padding: 0 15px;
    gap: 30px;
    align-items: center; 
  }
`;

const Sidebar = styled.div`
  width: 280px;
  flex-shrink: 0;
  position: sticky;
  top: 100px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 1024px) {
    width: 100%;
    max-width: 800px; 
    position: static;
    height: auto;
  }
`;

const FilterSection = styled.div`
  background: linear-gradient(145deg, #0a0a0a, #1a1a1a);
  border: 1px solid #333;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const FilterTitle = styled.h3`
  font-family: 'Quattrocento', serif;
  color: #fff;
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.3rem;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #444;
  padding-bottom: 12px;
  text-align: center;
  font-weight: 600;

  @media (min-width: 1025px) {
    text-align: left;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;

  @media (min-width: 1025px) {
    justify-content: flex-start;
  }
`;

const FilterChip = styled(motion.button)`
  background: ${props => props.active ? 'linear-gradient(45deg, #fff, #e0e0e0)' : 'rgba(255, 255, 255, 0.05)'};
  color: ${props => props.active ? '#000' : '#bbb'};
  border: 1px solid ${props => props.active ? '#fff' : '#444'};
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-family: 'Quattrocento', serif;
  font-size: 0.9rem;
  font-weight: 600;
  outline: none;
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.active ? '0 4px 15px rgba(255, 255, 255, 0.3)' : 'none'};
  user-select: none;

  &:hover {
    background: ${props => props.active ? 'linear-gradient(45deg, #fff, #e0e0e0)' : 'rgba(255, 255, 255, 0.15)'};
    color: ${props => props.active ? '#000' : '#fff'};
    border-color: ${props => props.active ? '#fff' : '#666'};
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 0.85rem;
    flex-grow: 1; 
    text-align: center;
    max-width: 150px; 
  }
`;

const Grid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 30px;
  flex-grow: 1;
  width: 100%;
  margin-bottom: 60px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 10px;
  }
`;

const NoResults = styled.div`
  color: #888;
  font-family: 'Quattrocento', serif;
  text-align: center;
  padding: 60px;
  font-size: 1.5rem;
  grid-column: 1 / -1;
  background: rgba(0,0,0,0.3);
  border-radius: 12px;
  border: 1px dashed #444;
`;

const ProjectsList = () => {
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedTools, setSelectedTools] = useState([]);

  const { allRoles, allTools } = useMemo(() => {
    const roles = new Set();
    const tools = new Set();
    projectsData.forEach(project => {
      project.jobTags.forEach(tag => roles.add(tag));
      project.tools.forEach(tool => tools.add(tool));
    });
    return {
      allRoles: Array.from(roles).sort(),
      allTools: Array.from(tools).sort()
    };
  }, []);

  const toggleRole = (role) => {
    setSelectedRoles(prev => {
      if (prev.includes(role)) {
        return prev.filter(r => r !== role);
      } else {
        return [...prev, role];
      }
    });
  };

  const toggleTool = (tool) => {
    setSelectedTools(prev => {
      if (prev.includes(tool)) {
        return prev.filter(t => t !== tool);
      } else {
        return [...prev, tool];
      }
    });
  };

  const filteredProjects = useMemo(() => {
    return projectsData.filter(project => {
      // Filter by Role (Union within roles)
      const matchesRole = selectedRoles.length === 0 || 
        selectedRoles.some(role => project.jobTags.includes(role));

      // Filter by Tool (Union within tools)
      const matchesTool = selectedTools.length === 0 || 
        selectedTools.some(tool => project.tools.includes(tool));

      // Intersect both filters
      return matchesRole && matchesTool;
    });
  }, [selectedRoles, selectedTools]);

  return (
    <Container>
      <HeaderContainer>
        <Computer />
      </HeaderContainer>
      
      <MainContent>
        <Sidebar>
          <FilterSection>
            <FilterTitle>Roles</FilterTitle>
            <FilterGroup>
              <FilterChip
                 active={selectedRoles.length === 0}
                 onClick={() => setSelectedRoles([])}
                 whileTap={{ scale: 0.95 }}
              >
                All
              </FilterChip>
              {allRoles.map(role => (
                <FilterChip 
                  key={role} 
                  active={selectedRoles.includes(role)}
                  onClick={() => toggleRole(role)}
                  whileTap={{ scale: 0.95 }}
                >
                  {role}
                </FilterChip>
              ))}
            </FilterGroup>
          </FilterSection>

          <FilterSection>
            <FilterTitle>Tech Stack</FilterTitle>
            <FilterGroup>
              <FilterChip
                 active={selectedTools.length === 0}
                 onClick={() => setSelectedTools([])}
                 whileTap={{ scale: 0.95 }}
              >
                All
              </FilterChip>
              {allTools.map(tool => (
                <FilterChip 
                  key={tool} 
                  active={selectedTools.includes(tool)}
                  onClick={() => toggleTool(tool)}
                  whileTap={{ scale: 0.95 }}
                >
                  {tool}
                </FilterChip>
              ))}
            </FilterGroup>
          </FilterSection>
        </Sidebar>

        <Grid layout>
          <AnimatePresence>
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                >
                  <ProjectCard project={project} />
                </motion.div>
              ))
            ) : (
              <NoResults>No projects match your filters.</NoResults>
            )}
          </AnimatePresence>
        </Grid>
      </MainContent>
    </Container>
  );
};

export default ProjectsList;
